import PropTypes from 'prop-types';
import React from 'react';
import transparentize from 'polished/lib/color/transparentize';
import { diffWordsWithSpace } from 'diff';
import HtmlDiff from 'htmldiff-js';
import styled from 'styled-components';
import { isEmpty } from 'lodash';

import Col from 'ravenjs/lib/Col';
import Pre from 'ravenjs/lib/Pre';
import Row from 'ravenjs/lib/Row';
import Typography from 'ravenjs/lib/Typography';
import { COLORS } from 'ravenjs/constants/colors';
import { THEME } from 'ravenjs/constants/theme';
import { themeGet } from 'ravenjs/utils/theme';

import { sanitizeMarkup } from 'utils/sanitize';
import { compareDuplicatedImages } from 'utils/common';
import { MIGRATED_COLORS } from 'constants/quartz/migrated_colors';

const DiffCheckerHtmlModalStyled = styled.div`
    margin: 20px;
`;

const DiffCheckerHtml = styled(Typography)`
    del {
        background-color: ${MIGRATED_COLORS.literalFfb6ba};
        img {
            border: 4px inset ${MIGRATED_COLORS.literalFfb6ba};
            background-color: transparent;
        }
    }

    ins {
        background-color: ${MIGRATED_COLORS.literal97f295};
        text-decoration: none !important;
        img {
            border: 4px inset ${MIGRATED_COLORS.literal97f295};
            background-color: transparent;
        }
    }

    del:has(img),
    ins:has(img) {
        background-color: transparent;
        height: 100%;
        position: relative;
        display: inline-block;
        margin: 8px 0;
        &::before {
            display: flex;
            justify-content: center;
            align-items: center;
            position: absolute;
            top: -12px;
            right: -12px;
            font-size: 12px;
            border-radius: 50%;
            width: 24px;
            height: 24px;
            z-index: 10;
        }
    }

    del:has(img) {
        &::before {
            content: '';
            background: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI4LjMuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCA4IDgiIHN0eWxlPSJlbmFibGUtYmFja2dyb3VuZDpuZXcgMCAwIDggODsiIHhtbDpzcGFjZT0icHJlc2VydmUiPgo8c3R5bGUgdHlwZT0idGV4dC9jc3MiPgoJLnN0MHtmaWxsLXJ1bGU6ZXZlbm9kZDtjbGlwLXJ1bGU6ZXZlbm9kZDtmaWxsOiNGRkZGRkY7fQo8L3N0eWxlPgo8cGF0aCBjbGFzcz0ic3QwIiBkPSJNNC44LDRsMy0zYzAuMi0wLjIsMC4yLTAuNiwwLTAuOEM3LjYtMC4xLDcuMi0wLjEsNywwLjJsLTMsM2wtMy0zYy0wLjItMC4yLTAuNi0wLjItMC44LDAKCWMtMC4yLDAuMi0wLjIsMC42LDAsMC44bDMsM2wtMywzYy0wLjIsMC4yLTAuMiwwLjYsMCwwLjhzMC42LDAuMiwwLjgsMGwzLTNsMywzYzAuMiwwLjIsMC42LDAuMiwwLjgsMGMwLjItMC4yLDAuMi0wLjYsMC0wLjhMNC44LDQKCXoiLz4KPC9zdmc+Cg==')
                no-repeat center center;
            background-size: 50%;
            background-color: ${MIGRATED_COLORS.literalB54124};
            color: ${MIGRATED_COLORS.literalFff};
        }
    }
    ins:has(img) {
        &::before {
            content: '';
            background: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDI4LjMuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPgo8c3ZnIHZlcnNpb249IjEuMSIgaWQ9IkxheWVyXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4IgoJIHZpZXdCb3g9IjAgMCA4IDgiIHN0eWxlPSJlbmFibGUtYmFja2dyb3VuZDpuZXcgMCAwIDggODsiIHhtbDpzcGFjZT0icHJlc2VydmUiPgo8c3R5bGUgdHlwZT0idGV4dC9jc3MiPgoJLnN0MHtmaWxsLXJ1bGU6ZXZlbm9kZDtjbGlwLXJ1bGU6ZXZlbm9kZDtmaWxsOiNGRkZGRkY7fQo8L3N0eWxlPgo8cGF0aCBjbGFzcz0ic3QwIiBkPSJNNy45LDEuMkM4LDEuNCw4LDEuNyw3LjksMS45TDMsNi44QzIuOCw3LDIuNSw3LDIuNCw2LjhMMC4xLDQuNUMwLDQuNCwwLDQuMSwwLjEsMy45YzAuMi0wLjIsMC41LTAuMiwwLjYsMAoJbDEuOSwxLjlsNC42LTQuNkM3LjQsMS4xLDcuNywxLjEsNy45LDEuMnoiLz4KPC9zdmc+Cg==')
                no-repeat center center;
            background-size: 50%;
            background-color: ${MIGRATED_COLORS.green1};
            color: ${MIGRATED_COLORS.literalFff};
        }
    }
`;

/**
 * A simple Diff-Checker component, to show the differences between two Strings.
 *
 * @method      DiffChecker
 * @param       {Object}    props Component props
 * @constructor
 */
function DiffChecker(props) {
    const {
        addedBgColor,
        addedTextBgColor,
        changedLabel,
        changedText,
        deletedBgColor,
        deletedTextBgColor,
        hideLabels,
        originalLabel,
        originalText,
        diffs,
        showHtmlDiff,
    } = props;

    let diffStrings = [];
    let diffHtml = '';

    if (showHtmlDiff) {
        diffHtml = HtmlDiff.execute(
            compareDuplicatedImages({ oldHtml: originalText, newHtml: changedText }),
            changedText
        );
    } else if (originalText === 'None' || !originalText || !changedText) {
        diffStrings = [
            {
                removed: true,
                value: originalText,
                added: undefined,
            },
            { removed: undefined, added: true, value: changedText },
        ];
    } else if (!isEmpty(diffs)) {
        diffStrings = diffs;
    } else {
        diffStrings = diffWordsWithSpace(originalText, changedText);
    }

    const bgColorAdded = themeGet(`palette.${addedBgColor}.color`, addedBgColor)({ theme: THEME });
    const bgColorDeleted = themeGet(
        `palette.${deletedBgColor}.color`,
        deletedBgColor
    )({
        theme: THEME,
    });
    // Find the background color for the added / deleted text blocks.
    const textBgAdded = themeGet(
        `palette.${addedTextBgColor}.color`,
        addedTextBgColor
    )({
        theme: THEME,
    });
    const textBgDeleted = themeGet(
        `palette.${deletedTextBgColor}.color`,
        deletedTextBgColor
    )({
        theme: THEME,
    });

    if (diffHtml) {
        return (
            <DiffCheckerHtmlModalStyled>
                <DiffCheckerHtml dangerouslySetInnerHTML={sanitizeMarkup(diffHtml)} />
            </DiffCheckerHtmlModalStyled>
        );
    }

    // Render teh diff checker.
    return (
        <Row gutter={false} alignItems="stretch">
            <Col size={{ xs: 12, md: 6 }} gutter={false}>
                <Row gutter={false} direction="column" height="100%" wrap="nowrap">
                    {!hideLabels && (
                        <Typography align="center" gutterBottom="5px">
                            {originalLabel}
                        </Typography>
                    )}
                    <Pre backgroundColor={bgColorDeleted} height="100%">
                        {diffStrings.map((part, partIdx) => {
                            const { added, count, removed, value } = part;

                            const key = `${count}__${value}__${partIdx}`;
                            // If we have not added the part, then render it.
                            if (!added) {
                                return (
                                    <Typography
                                        as="span"
                                        backgroundColor={removed ? textBgDeleted : null}
                                        color={MIGRATED_COLORS.literal333}
                                        gutterBottom="0"
                                        key={key}
                                        type="pre"
                                    >
                                        {value}
                                    </Typography>
                                );
                            }
                            return null;
                        })}
                    </Pre>
                </Row>
            </Col>
            <Col size={{ xs: 12, md: 6 }} gutter={false}>
                <Row gutter={false} direction="column" height="100%" wrap="nowrap">
                    {!hideLabels && (
                        <Typography align="center" gutterBottom="5px">
                            {changedLabel}
                        </Typography>
                    )}
                    <Pre backgroundColor={bgColorAdded} height="100%">
                        {diffStrings.map((part, partIdx) => {
                            const { added, count, removed, value } = part;
                            const key = `${count}__${value}__${partIdx}`;
                            // If we have not removed the part, then render it.
                            if (!removed) {
                                return (
                                    <Typography
                                        as="span"
                                        backgroundColor={added ? textBgAdded : null}
                                        color={MIGRATED_COLORS.literal333}
                                        gutterBottom="0"
                                        key={key}
                                        type="pre"
                                    >
                                        {value}
                                    </Typography>
                                );
                            }
                            return null;
                        })}
                    </Pre>
                </Row>
            </Col>
        </Row>
    );
}

DiffChecker.propTypes = {
    /**
     * Set the `background-color` css property, for the **added** container.
     */
    addedBgColor: PropTypes.string,
    /**
     * Set the `background-color` css property, for the **added** text blocks.
     */
    addedTextBgColor: PropTypes.string,
    /**
     * The changed text box label.
     */
    changedLabel: PropTypes.string,
    /**
     * The changed text to diff for.
     */
    changedText: PropTypes.string,
    /**
     * Set the `background-color` css property, for the **deleted** container.
     */
    deletedBgColor: PropTypes.string,
    /**
     * Set the `background-color` css property, for the **deleted** text blocks.
     */
    deletedTextBgColor: PropTypes.string,
    /**
     * Hide the labels for the original and changed columns.
     */
    hideLabels: PropTypes.bool,
    /**
     * The original text box label.
     */
    originalLabel: PropTypes.string,
    /**
     * The original text to diff against.
     */
    originalText: PropTypes.string,
    diffs: PropTypes.array,
    showHtmlDiff: PropTypes.bool,
};

DiffChecker.defaultProps = {
    addedBgColor: transparentize(0.9, COLORS.PRE_ADDED),
    addedTextBgColor: COLORS.PRE_ADDED,
    changedLabel: 'Changed Text',
    changedText: '',
    deletedBgColor: transparentize(0.9, COLORS.PRE_DELETED),
    deletedTextBgColor: COLORS.PRE_DELETED,
    diffs: [],
    hideLabels: null,
    originalLabel: 'Original Text',
    originalText: '',
    showHtmlDiff: false,
};

export default DiffChecker;
